import _ from 'lodash';
import React, { useEffect, useState } from 'react';

// Data
import { IFinalizeSessionFields } from './approve-groupings.interface';
import { readUserInputMessage } from '../../../lib/helpers/graphql-error.helpers';
import { useSingleFlight } from '../../../lib/hooks/use-single-flight';
import { messages } from './approve-groupings.messages';

// Components
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import ApproveGroupings from './approve-groupings.component';

interface IApproveGroupingsContainer {
  plans: IAssessmentStagePlan[];
  playerAssessments: IPlayerAssessment[];
  drills: IDrill[];
  practicePlans: IPracticePlan[]
  finalize: Function;
  loading: boolean;
  fetchError: boolean;
  fetchErrorMessage?: string | null;
}

const ApproveGroupingsContainer: React.FC<IApproveGroupingsContainer> = ({
  plans,
  playerAssessments,
  drills,
  practicePlans,
  finalize,
  loading,
  fetchError,
  fetchErrorMessage
}) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<React.ReactNode>(null);
  const [sessionSets, setSessionSets] = useState<IFinalizeSessionFields[]>([]);
  // The server refuses a set that leaves an active player out unless it's named here.
  const [removedPlayerAssessmentIds, setRemovedPlayerAssessmentIds] = useState<string[]>([]);
  const [playerAssessmentsById, setPlayerAssessmentsById] = useState<{ [key: string]: IPlayerAssessment }>({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const nextSessionSets: IFinalizeSessionFields[] = [];

    plans.forEach(plan => {
      plan.sessionSets.forEach(sessionSet => {
        const drillsByKey: { [key: string]: IDrill } = _.keyBy(drills, '_id');
        const currDrills = plan.sessionStage.config.drills.map((drillId: string) => drillsByKey[drillId]);
        const practicePlansByKey: { [key: string]: IPracticePlan } = _.keyBy(practicePlans, '_id');
        const currPracticePlans = plan.sessionStage.config.practicePlans.map((practicePlanId: string) => practicePlansByKey[practicePlanId]);

        _.pull(currDrills, undefined);
        _.pull(currPracticePlans, undefined);

        nextSessionSets.push({
          _id: sessionSet.session._id,
          stageId: plan.sessionStage._id,
          playerAssessments: [...(sessionSet.group ?? [])],
          drills: currDrills,
          practicePlans: currPracticePlans,
        })
      });
    });


    setSessionSets(nextSessionSets);
    setRemovedPlayerAssessmentIds([]);
  }, [plans]);

  useEffect(() => {
    setPlayerAssessmentsById(_.keyBy(playerAssessments, '_id'));
  }, [playerAssessments])

  const buildRemoveItem = (listKey: string) =>
    (item: any, sessionIndex: number) => {
      // @ts-ignore
      _.remove(sessionSets[sessionIndex][listKey], currItem => currItem._id === item._id);

      setSessionSets([...sessionSets]);
    };

  const buildAddItem = (listKey: string) =>
    (item: any, sessionIndex: number) => {
      // @ts-ignore
      sessionSets[sessionIndex][listKey] = [...sessionSets[sessionIndex][listKey], item];
      setSessionSets([...sessionSets]);
    };

  const removePlayerAssessment = (playerAssessment: IPlayerAssessment, sessionIndex: number) => {
    setRemovedPlayerAssessmentIds((ids) => _.union(ids, [playerAssessment._id]));
    buildRemoveItem('playerAssessments')(playerAssessment, sessionIndex);
  };

  // Scheduled only once the server confirms: a refusal (e.g. session limits that leave players out) stays on screen.
  const finalizeFlight = useSingleFlight(async (finalSessionSets: IFinalizeSessionFields[]) => {
    setSubmitError(null);
    try {
      await finalize(finalSessionSets, removedPlayerAssessmentIds);
      setHasSubmitted(true);
    } catch (error) {
      console.error('Finalizing groupings failed', error);
      setSubmitError(readUserInputMessage(error) || <IntlMsg {...messages.finalizeFailed} />);
    }
  });

  return <ApproveGroupings
    plans={plans}
    sessionSets={sessionSets}
    playerAssessments={playerAssessments}
    playerAssessmentsById={playerAssessmentsById}
    drills={drills}
    practicePlans={practicePlans}
    onSubmit={finalizeFlight.run}
    isSubmitting={finalizeFlight.inFlight}
    submitError={submitError}
    loading={loading}
    fetchError={fetchError}
    fetchErrorMessage={fetchErrorMessage}
    removePlayerAssessment={removePlayerAssessment}
    removeDrill={buildRemoveItem('drills')}
    removePracticePlan={buildRemoveItem('practicePlans')}
    addPlayerAssessment={buildAddItem('playerAssessments')}
    addDrill={buildAddItem('drills')}
    addPracticePlan={buildAddItem('practicePlans')}
    hasSubmitted={hasSubmitted}
  />;
};

export default ApproveGroupingsContainer;
